import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cleanTechniqueName } from '@/lib/utils';

interface PartnerFeedbackModalProps {
  isOpen: boolean;
  onClose: (wasSaved?: boolean) => void;
  partner: {
    id: string;
    partner_name: string;
    partner_username?: string;
    partner_avatar_url?: string;
    partner_belt_rank?: string;
    partner_user_id?: string;
  };
  trainingId: string | null;
  trainingNotesId?: string | null; // Add training notes ID
  existingFeedback?: FeedbackData | null;
}

interface FeedbackData {
  id?: string;
  technique_ids: string[];
  feedback_text?: string;
  created_at?: string;
}

// Componente reutilizável para técnicas com collapsible
const TechniqueCollapsible = ({ 
  selectedTechniques = [], 
  onTechniqueToggle, 
  readOnly = false,
  getTechniqueName,
  techniques = []
}: {
  selectedTechniques?: string[];
  onTechniqueToggle?: (technique: string) => void;
  readOnly?: boolean;
  getTechniqueName?: (techniqueId: string) => string;
  techniques?: any[];
}) => {
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName]
    );
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  if (readOnly) {
    // Modo somente leitura - mostrar apenas técnicas selecionadas
    return (
      <div className="space-y-3">
        {selectedTechniques.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedTechniques.map((techniqueId) => {
              // Find the technique by ID in the techniques array
              const technique = techniques.find(t => t.id.toString() === techniqueId);
              const techniqueName = technique ? technique.name : techniqueId;
              
              return (
                <Badge key={techniqueId} variant="secondary" className="text-xs bg-[#D72E16] text-white">
                  {getTechniqueName ? getTechniqueName(techniqueId) : techniqueName}
                </Badge>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-xs text-gray-500">No techniques selected</p>
          </div>
        )}
      </div>
    );
  }

  // Modo interativo - mostrar collapsible com todas as técnicas
  return (
    <div className="space-y-3">
      {/* Collapsible com todas as técnicas */}
      <div className="space-y-3">
        {/* Group techniques by category */}
        {(() => {
          const techniquesByCategory = techniques.reduce((acc, technique) => {
            const category = technique.category || 'Uncategorized';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(technique);
            return acc;
          }, {} as Record<string, any[]>);

          return Object.entries(techniquesByCategory).map(([categoryName, categoryTechniques]) => (
            <Collapsible 
              key={categoryName} 
              open={isCategoryOpen(categoryName)} 
              onOpenChange={() => toggleCategory(categoryName)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <span>{categoryName}</span>
                {isCategoryOpen(categoryName) ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="flex flex-wrap gap-1">
                  {(categoryTechniques as any[]).map((technique) => {
                    const techniqueId = technique.id.toString();
                    const isSelected = selectedTechniques.includes(techniqueId);
                    return (
                      <button
                        key={techniqueId}
                        type="button"
                        onClick={() => onTechniqueToggle?.(techniqueId)}
                        className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                          isSelected
                            ? 'bg-[#D72E16] text-white border-[#D72E16]'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {getTechniqueName ? getTechniqueName(techniqueId) : technique.name}
                      </button>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ));
        })()}
      </div>
    </div>
  );
};

export const PartnerFeedbackModal = ({
  isOpen,
  onClose,
  partner,
  trainingId,
  trainingNotesId,
  existingFeedback
}: PartnerFeedbackModalProps) => {
  const { toast } = useToast();
  
  // Debug: Log partner info
  useEffect(() => {
    if (isOpen) {
      // Removed debug logs
    }
  }, [isOpen, partner]);

  // If no trainingId is provided, show a message and close
  if (!trainingId) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
          <SossietyDialogHeader 
            title="Cannot Add Feedback"
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={onClose} />
          </SossietyDialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              You need to save your training notes first before adding feedback for partners.
            </p>
            
            <Button
              onClick={() => onClose()}
              className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white rounded-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const [feedback, setFeedback] = useState<FeedbackData>({
    technique_ids: [],
    feedback_text: ''
  });
  const [techniques, setTechniques] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [techniquesLoading, setTechniquesLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showTechniques, setShowTechniques] = useState(false);

  // Initialize feedback state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingFeedback) {
        // Normalize technique_ids to ensure they are all strings
        const normalizedFeedback = {
          ...existingFeedback,
          technique_ids: existingFeedback.technique_ids.map(id => id.toString())
        };
        setFeedback(normalizedFeedback);
      } else {
        setFeedback({
          technique_ids: [],
          feedback_text: ''
        });
      }
    }
  }, [isOpen, existingFeedback]);

  // Load techniques for selection
  useEffect(() => {
    if (isOpen) {
      loadTechniques();
    }
  }, [isOpen, partner.id]);

  const loadTechniques = async () => {
    if (!trainingId) {
      setTechniques([]);
      return;
    }

    try {
      setTechniquesLoading(true);
      
      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        console.error('No access token available');
        setTechniques([]);
        return;
      }
      
      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_techniques`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.techniques) {
        setTechniques(result.techniques);
      } else {
        setTechniques([]);
      }
    } catch (error) {
      console.error('Error loading techniques:', error);
      setTechniques([]);
    } finally {
      setTechniquesLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Ensure all technique_ids are strings
      const normalizedTechniqueIds = feedback.technique_ids.map(id => id.toString());
      
      console.log('🔍 handleSave - Original technique_ids:', feedback.technique_ids);
      console.log('🔍 handleSave - Normalized technique_ids:', normalizedTechniqueIds);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const requestBody = {
        training_notes_id: trainingNotesId,
        partner_id: partner.partner_user_id || partner.id,
        technique_ids: normalizedTechniqueIds,
        feedback_text: feedback.feedback_text || ''
      };

      console.log('🔍 handleSave - Request body:', requestBody);

      const response = await fetch(`${baseUrl}/functions/v1/save_partner_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        
        
        toast({
          title: "Feedback saved successfully!",
          description: "Your feedback has been saved.",
        });
        
        onClose(true);
      } else {
        const errorText = await response.text();
        console.error('Error saving feedback:', errorText);
        toast({
          title: "Error",
          description: "Failed to save feedback. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: "Error",
        description: "Failed to save feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTechniqueToggle = (technique: string) => {
    setFeedback(prev => {
      const newTechniqueIds = prev.technique_ids.includes(technique)
        ? prev.technique_ids.filter(id => id !== technique)
        : [...prev.technique_ids, technique];
      
      return {
        ...prev,
        technique_ids: newTechniqueIds
      };
    });
  };

  const handleClose = () => {
    // Só mostrar confirmação se estiver editando (não visualizando)
    if (!existingFeedback && (feedback.technique_ids.length > 0 || feedback.feedback_text.trim())) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const getTechniqueName = (techniqueId: string) => {
    // Convert to string to ensure consistency
    const idStr = techniqueId.toString();
    
    // First try to find by ID
    const technique = techniques.find(t => t.id.toString() === idStr);
    
    if (technique) {
      const cleanedName = cleanTechniqueName(technique.name);
      return cleanedName;
    }
    
    // If not found by ID, try to find by name (in case techniques are saved as names)
    const techniqueByName = techniques.find(t => t.name === idStr);
    
    if (techniqueByName) {
      const cleanedName = cleanTechniqueName(techniqueByName.name);
      return cleanedName;
    }
    
    // If the techniqueId looks like a number, it might be a direct technique name
    // This can happen when techniques are saved as names instead of IDs
    if (!isNaN(Number(idStr))) {
      // Try to find by index (assuming the number is a position in the techniques array)
      const techniqueByIndex = techniques[Number(idStr) - 1];
      
      if (techniqueByIndex) {
        const cleanedName = cleanTechniqueName(techniqueByIndex.name);
        return cleanedName;
      }
    }
    
    // If not found by ID, name, or index, return the techniqueId as is
    const cleanedId = cleanTechniqueName(idStr);
    return cleanedId;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ').filter(word => word.length > 0);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="w-[90vw] max-w-xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto"
          id="partner-feedback-dialog"
          aria-describedby="partner-feedback-description"
        >
          <DialogDescription id="partner-feedback-description" className="sr-only">
            Provide feedback for your training partner {partner.partner_name}
          </DialogDescription>
          <SossietyDialogHeader 
            title={existingFeedback ? "Feedback" : "Give feedback"}
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={handleClose} />
          </SossietyDialogHeader>

          <div className="space-y-4">
            {/* Praise text - only show when no existing feedback */}
            {!existingFeedback && (
              <p className="text-sm text-gray-600">
                Praise {partner.partner_name} and highlight what they did well in training today!
              </p>
            )}

            {/* Partner Info - Aligned to the left and closer to the text above */}
            <div className="flex items-center justify-center space-x-3 px-3">
              <FramedAvatar
                src={partner.partner_avatar_url}
                alt={partner.partner_name}
                beltRank={partner.partner_belt_rank}
                size={40}
                initials={getInitials(partner.partner_name)}
              />
              <div className="text-left">
                <p className="text-sm font-medium">{partner.partner_name}</p>
                {partner.partner_username && (
                  <p className="text-xs text-gray-500">@{partner.partner_username}</p>
                )}
              </div>
            </div>

            {feedback.created_at && (
              <div className="text-xs text-gray-500 text-left">
                Feedback given on {formatDate(feedback.created_at)}
              </div>
            )}

            <div className="space-y-4">
              {/* Techniques Selection */}
              <div className="space-y-4">
                <label className="text-sm font-medium">
                  {existingFeedback 
                    ? `Techniques ${partner.partner_name} did well` 
                    : `Which techniques ${partner.partner_name} did well?`
                  }
                  {!existingFeedback && (
                    <button
                      type="button"
                      onClick={() => setShowTechniques(!showTechniques)}
                      className="ml-2 text-xs text-[#D72E16] underline hover:no-underline"
                    >
                      {showTechniques ? 'Hide' : 'Show'} Techniques
                    </button>
                  )}
                </label>
                
                {/* Always show selected techniques count */}
                {!existingFeedback && feedback.technique_ids.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Selected techniques: {feedback.technique_ids.length}
                  </div>
                )}
                
                {/* Always show selected techniques pills */}
                {!existingFeedback && feedback.technique_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 !mt-2">
                    {feedback.technique_ids.map((techniqueId) => {
                      // Find the technique by ID in the techniques array
                      const technique = techniques.find(t => t.id.toString() === techniqueId);
                      const techniqueName = technique ? technique.name : techniqueId;
                      
                      return (
                        <Badge 
                          key={techniqueId} 
                          variant="secondary" 
                          className="text-xs bg-[#D72E16] text-white hover:bg-[#B8251A] cursor-pointer"
                          onClick={() => handleTechniqueToggle(techniqueId)}
                        >
                          {getTechniqueName ? getTechniqueName(techniqueId) : techniqueName} ×
                        </Badge>
                      );
                    })}
                  </div>
                )}
                
                {/* Show techniques for existing feedback (view mode) */}
                {existingFeedback && feedback.technique_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {feedback.technique_ids.map((techniqueId) => {
                      // Find the technique by ID in the techniques array
                      const technique = techniques.find(t => t.id.toString() === techniqueId);
                      const techniqueName = technique ? technique.name : techniqueId;
                      
                      return (
                        <Badge 
                          key={techniqueId} 
                          variant="secondary" 
                          className="text-xs bg-[#D72E16] text-white"
                        >
                          {getTechniqueName ? getTechniqueName(techniqueId) : techniqueName}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                
                {showTechniques && (
                  <TechniqueCollapsible
                    selectedTechniques={feedback.technique_ids}
                    onTechniqueToggle={handleTechniqueToggle}
                    readOnly={false}
                    getTechniqueName={getTechniqueName}
                    techniques={techniques}
                  />
                )}
              </div>

              {/* Feedback Text */}
              <div className="space-y-2">
                <label htmlFor="feedback-text" className="text-sm font-medium">
                  {existingFeedback ? "Your feedback" : "Share your feedback"}
                </label>
                {existingFeedback ? (
                  <div className="text-sm text-gray-900 mt-1">
                    {feedback.feedback_text || "No feedback text provided"}
                  </div>
                ) : (
                  <Textarea
                    id="feedback-text"
                    value={feedback.feedback_text}
                    onChange={(e) => setFeedback(prev => ({ ...prev, feedback_text: e.target.value }))}
                    placeholder="Write your feedback here."
                    className="min-h-[100px] text-sm"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                onClick={existingFeedback ? handleClose : handleSave} 
                disabled={loading}
                className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white rounded-full"
              >
                {loading ? 'Saving...' : (existingFeedback ? 'Close' : 'Save Feedback')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};